import { useState, useCallback, useEffect } from 'react';
import logger from '../utils/logger';

// Session storage constants
const SEARCH_SESSION_KEY = 'diligence_desk_search_session';

/** Default activity window: first of month 24 months ago → last day of current month. Wells stay unrestricted. */
export const getDefaultActivityDateFilters = () => {
  const now = new Date();
  const endYear = now.getFullYear();
  const endMonth = now.getMonth(); // 0-indexed
  const start = new Date(endYear, endMonth - 24, 1);
  const startYear = start.getFullYear();
  const startMonth = start.getMonth() + 1;
  const endMonth1 = endMonth + 1;
  const lastDay = new Date(endYear, endMonth1, 0).getDate();

  return {
    start_date: `${startYear}-${String(startMonth).padStart(2, '0')}-01`,
    end_date: `${endYear}-${String(endMonth1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
  };
};

// Session storage utilities
const saveSearchSession = (searchData) => {
  // First, try to save the full session data (including searchResults)
  try {
    const jsonString = JSON.stringify(searchData);
    sessionStorage.setItem(SEARCH_SESSION_KEY, jsonString);
    // Success - full data saved
    return true;
  } catch (error) {
    logger.error('Error saving to sessionStorage:', error);
    if (error.name === 'QuotaExceededError') {
      logger.warn('STORAGE QUOTA EXCEEDED! Falling back to parameters-only save.');
      
      // Fallback: Save without searchResults
      try {
        const { searchResults, ...sessionDataWithoutResults } = searchData;
        const fallbackData = {
          ...sessionDataWithoutResults,
          _fallbackMode: true // Flag to indicate this is a fallback save
        };
        const jsonString = JSON.stringify(fallbackData);
        sessionStorage.setItem(SEARCH_SESSION_KEY, jsonString);
        logger.debug('Session saved in fallback mode (parameters only)');
        return false; // Indicates fallback mode was used
      } catch (fallbackError) {
        logger.error('Failed to save even without searchResults:', fallbackError);
        return false;
      }
    }
    // Other errors - don't save anything
    return false;
  }
};

const loadSearchSession = () => {
  try {
    const savedData = sessionStorage.getItem(SEARCH_SESSION_KEY);
    return savedData ? JSON.parse(savedData) : null;
  } catch (error) {
    logger.error('Error loading from sessionStorage:', error);
    return null;
  }
};

const clearSearchSession = () => {
  sessionStorage.removeItem(SEARCH_SESSION_KEY);
};

export const useSearchData = (token, showNotification, resetAnalytics, datasetFromRoute = null) => {
  // Search state
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Abort controller for search cancellation
  const [abortController, setAbortController] = useState(null);

  // Search parameters
  const [searchParams, setSearchParams] = useState({
    state: '',
    search_production: true,
    search_severances: true,
    search_wells: true,
    search_inspections: true,
    search_violations: true,
    search_spills: true,
    operator_name: '',
    operator_number: '',
    lease_name: '',
    lease_number: '',
    county: '',
    api_number: '',
  });

  // Input values (for immediate UI updates)
  const [inputValues, setInputValues] = useState({
    operator_name: '',
    operator_number: '',
    lease_name: '',
    lease_number: '',
    county: '',
    api_number: '',
  });

  // Date filters — activity defaults to last 24 calendar months; wells stay unrestricted
  const [activityDateFilters, setActivityDateFilters] = useState(getDefaultActivityDateFilters);

  const [wellDateFilters, setWellDateFilters] = useState({
    start_date: '',
    end_date: '',
  });

  // Well group filters (all unchecked by default to capture all records including NULL/empty well_group)
  const [wellGroupFilters, setWellGroupFilters] = useState({
    'Active': false,
    'Permit': false,
    'Shut In': false,
    'TA': false,
    'Partial Plug': false,
    'P&A': false,
    'Dry Hole': false,
  });

  // UI state
  const [mainFiltersExpanded, setMainFiltersExpanded] = useState(true);
  const [otherFiltersExpanded, setOtherFiltersExpanded] = useState(false);
  const [dateFiltersExpanded, setDateFiltersExpanded] = useState(false);
  const [multiLineEnabled, setMultiLineEnabled] = useState(false);

  // Initialize state from route parameter
  useEffect(() => {
    if (datasetFromRoute) {
      // Convert route param to state format (e.g., 'texas' -> 'Texas')
      const stateFromRoute = datasetFromRoute.charAt(0).toUpperCase() + datasetFromRoute.slice(1).toLowerCase();
      
      // If state doesn't match route, update it
      if (searchParams.state !== stateFromRoute) {
        setSearchParams(prev => ({ 
          ...prev, 
          state: stateFromRoute,
          // For Texas, ensure Wells is always selected
          search_wells: stateFromRoute === 'Texas' ? true : prev.search_wells
        }));
      }
    }
  }, [datasetFromRoute, searchParams.state]);

  // Ensure Wells is always selected for Texas
  useEffect(() => {
    if (searchParams.state === 'Texas' && !searchParams.search_wells) {
      setSearchParams(prev => ({ ...prev, search_wells: true }));
    }
  }, [searchParams.state, searchParams.search_wells]);

  // Validation function
  const validateSearchParameters = useCallback(() => {
    // Only validate if a state is selected
    if (searchParams.state) {
      // Check if at least one table is selected
      const hasTableSelected = searchParams.search_production || 
                             searchParams.search_severances || 
                             searchParams.search_wells || 
                             searchParams.search_inspections || 
                             searchParams.search_violations || 
                             searchParams.search_spills;

      if (!hasTableSelected) {
        return {
          isValid: false,
          error: 'Please select at least one table to search.'
        };
      }

      // Check for main search criteria (only core geographic/entity criteria)
      const hasMainSearchCriteria = (searchParams.operator_name && searchParams.operator_name.trim()) ||
                                   (searchParams.operator_number && searchParams.operator_number.trim()) ||
                                   (searchParams.lease_name && searchParams.lease_name.trim()) ||
                                   (searchParams.lease_number && searchParams.lease_number.trim()) ||
                                   (searchParams.county && searchParams.county.trim()) ||
                                   (searchParams.api_number && searchParams.api_number.trim());

      if (!hasMainSearchCriteria) {
        return {
          isValid: false,
          error: 'Please provide at least one main search criterion (operator name, operator number, lease name, lease number, county, or well API number).'
        };
      }
    }

    return { isValid: true };
  }, [searchParams]);

  // Search function
  const handleSearch = useCallback(async () => {
    // Sync all input values to search params before validation
    setSearchParams(prev => ({
      ...prev,
      operator_name: inputValues.operator_name,
      operator_number: inputValues.operator_number,
      lease_name: inputValues.lease_name,
      lease_number: inputValues.lease_number,
      county: inputValues.county,
    }));

    // Validate search parameters first
    const validation = validateSearchParameters();
    if (!validation.isValid) {
      showNotification(validation.error, 'error');
      return;
    }

    // Cancel any existing search first
    if (abortController) {
      abortController.abort();
    }
    
    // Create new abort controller for this search
    const newAbortController = new AbortController();
    setAbortController(newAbortController);
    
    // Clear any existing search session before starting new search
    clearSearchSession();
    setLoading(true);
    setError('');
    setSearchResults(null);
    
    // Show search in progress notification
    showNotification('Search in progress...', 'info');
    
    try {
      // Helper function to get valid date parameters
      const getValidDateParam = (dateValue) => {
        // Only return the date if it's complete (has both year and month)
        if (dateValue && dateValue.includes('-') && dateValue.split('-')[1] && dateValue.split('-')[1].length === 2) {
          return dateValue;
        }
        return null;
      };
      
      // Parse multi-line inputs into arrays
      const params = {
        ...searchParams,
        operator_name: searchParams.operator_name ? searchParams.operator_name.split('\n').filter(x => x.trim()) : null,
        operator_number: searchParams.operator_number ? searchParams.operator_number.split('\n').filter(x => x.trim()) : null,
        lease_name: searchParams.lease_name ? searchParams.lease_name.split('\n').filter(x => x.trim()) : null,
        lease_number: searchParams.lease_number ? searchParams.lease_number.split('\n').filter(x => x.trim()) : null,
        county: searchParams.county ? searchParams.county.split('\n').filter(x => x.trim()) : null,
        api_number: searchParams.api_number ? searchParams.api_number.split('\n').filter(x => x.trim()) : null,
        // Use consolidated date filters
        production_start_date: getValidDateParam(activityDateFilters.start_date),
        production_end_date: getValidDateParam(activityDateFilters.end_date),
        well_start_date: getValidDateParam(wellDateFilters.start_date),
        well_end_date: getValidDateParam(wellDateFilters.end_date),
        inspection_start_date: getValidDateParam(activityDateFilters.start_date),
        inspection_end_date: getValidDateParam(activityDateFilters.end_date),
        violation_start_date: getValidDateParam(activityDateFilters.start_date),
        violation_end_date: getValidDateParam(activityDateFilters.end_date),
        severance_start_date: getValidDateParam(activityDateFilters.start_date),
        severance_end_date: getValidDateParam(activityDateFilters.end_date),
        spill_start_date: getValidDateParam(activityDateFilters.start_date),
        spill_end_date: getValidDateParam(activityDateFilters.end_date),
        // Well group filters
        well_group: Object.keys(wellGroupFilters).filter(group => wellGroupFilters[group]),
      };

      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      const response = await fetch(`${API_BASE_URL}/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...params,
          state: searchParams.state
        }),
        signal: newAbortController.signal,
      });

      if (!response.ok) {
        let errorMessage = `Search failed: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch (e) {
          // If JSON parsing fails, try text
          try {
            const errorText = await response.text();
            if (errorText) {
              errorMessage = errorText;
            }
          } catch (textError) {
            logger.error('Could not parse error response:', textError);
          }
        }
        logger.error('Search API error:', response.status, errorMessage);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setSearchResults(data);
      
      // Update searchParams with actual params used by backend (may include converted lease_number/county from api_number)
      if (data.search_params) {
        const backendParams = data.search_params;
        const updatedParams = {
          ...searchParams,
          // Convert arrays back to newline-separated strings for display
          operator_name: backendParams.operator_name ? (Array.isArray(backendParams.operator_name) ? backendParams.operator_name.join('\n') : backendParams.operator_name) : searchParams.operator_name,
          operator_number: backendParams.operator_number ? (Array.isArray(backendParams.operator_number) ? backendParams.operator_number.join('\n') : backendParams.operator_number) : searchParams.operator_number,
          lease_name: backendParams.lease_name ? (Array.isArray(backendParams.lease_name) ? backendParams.lease_name.join('\n') : backendParams.lease_name) : searchParams.lease_name,
          lease_number: backendParams.lease_number ? (Array.isArray(backendParams.lease_number) ? backendParams.lease_number.join('\n') : backendParams.lease_number) : searchParams.lease_number,
          county: backendParams.county ? (Array.isArray(backendParams.county) ? backendParams.county.join('\n') : backendParams.county) : searchParams.county,
          api_number: backendParams.api_number ? (Array.isArray(backendParams.api_number) ? backendParams.api_number.join('\n') : backendParams.api_number) : searchParams.api_number,
        };
        setSearchParams(updatedParams);
        
        // Also update inputValues to match
        setInputValues(prev => ({
          ...prev,
          operator_name: updatedParams.operator_name || '',
          operator_number: updatedParams.operator_number || '',
          lease_name: updatedParams.lease_name || '',
          lease_number: updatedParams.lease_number || '',
          county: updatedParams.county || '',
          api_number: updatedParams.api_number || '',
        }));
      }
      
      // Try to save full session data (with searchResults)
      // If quota exceeded, saveSearchSession will automatically fall back to parameters-only
      const sessionData = {
        searchResults: data, // Try to include results
        searchParams: data.search_params ? {
          ...searchParams,
          // Use backend params if available (includes converted values)
          operator_name: data.search_params.operator_name ? (Array.isArray(data.search_params.operator_name) ? data.search_params.operator_name.join('\n') : data.search_params.operator_name) : searchParams.operator_name,
          operator_number: data.search_params.operator_number ? (Array.isArray(data.search_params.operator_number) ? data.search_params.operator_number.join('\n') : data.search_params.operator_number) : searchParams.operator_number,
          lease_name: data.search_params.lease_name ? (Array.isArray(data.search_params.lease_name) ? data.search_params.lease_name.join('\n') : data.search_params.lease_name) : searchParams.lease_name,
          lease_number: data.search_params.lease_number ? (Array.isArray(data.search_params.lease_number) ? data.search_params.lease_number.join('\n') : data.search_params.lease_number) : searchParams.lease_number,
          county: data.search_params.county ? (Array.isArray(data.search_params.county) ? data.search_params.county.join('\n') : data.search_params.county) : searchParams.county,
          api_number: data.search_params.api_number ? (Array.isArray(data.search_params.api_number) ? data.search_params.api_number.join('\n') : data.search_params.api_number) : searchParams.api_number,
        } : searchParams,
        // Include consolidated date filters
        activityDateFilters: activityDateFilters,
        wellDateFilters: wellDateFilters,
        // Include well group filters
        wellGroupFilters: wellGroupFilters,
        // Include UI state
        dateFiltersExpanded: dateFiltersExpanded,
        mainFiltersExpanded: mainFiltersExpanded,
        otherFiltersExpanded: otherFiltersExpanded,
        multiLineEnabled: multiLineEnabled
      };
      saveSearchSession(sessionData);

      // Show success notification
      showNotification('Search completed successfully!', 'success');

    } catch (err) {
      // Check if the error is due to abort
      if (err.name === 'AbortError') {
        logger.debug('Search was cancelled');
        return; // Don't show error for cancelled searches
      }
      
      setError(err.message);
      
      // Check if it's a row limit error and provide specific guidance
      if (err.message && (err.message.includes('10,000') || err.message.includes('60,000')) && err.message.includes('result limit')) {
        showNotification(err.message, 'error');
      } else if (err.message && err.message.includes('search parameter is required')) {
        showNotification(err.message, 'error');
      } else {
        // Generic error handling
        showNotification(`Search failed: ${err.message}`, 'error');
      }
    } finally {
      setLoading(false);
      setAbortController(null); // Clear abort controller when done
    }
  }, [
    searchParams,
    inputValues,
    activityDateFilters,
    wellDateFilters,
    wellGroupFilters,
    dateFiltersExpanded,
    mainFiltersExpanded,
    otherFiltersExpanded,
    multiLineEnabled,
    validateSearchParameters,
    showNotification,
    token,
    abortController
  ]);

  // Cancel search function
  const cancelSearch = useCallback(() => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setLoading(false);
      showNotification('Search cancelled', 'info');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abortController, showNotification]);

  // Reset function
  const handleReset = useCallback(() => {
    // Cancel any active search first
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
    
    setSearchParams({
      state: '',
      search_production: true,
      search_severances: true,
      search_wells: true,
      search_inspections: true,
      search_violations: true,
      search_spills: true,
      operator_name: '',
      operator_number: '',
      lease_name: '',
      lease_number: '',
      county: '',
      api_number: '',
    });
    setInputValues({
      operator_name: '',
      operator_number: '',
      lease_name: '',
      lease_number: '',
      county: '',
    });
    setActivityDateFilters(getDefaultActivityDateFilters());
    setWellDateFilters({
      start_date: '',
      end_date: '',
    });
    setWellGroupFilters({
      'Active': false,
      'Permit': false,
      'Shut In': false,
      'TA': false,
      'Partial Plug': false,
      'P&A': false,
      'Dry Hole': false,
    });
    setSearchResults(null);
    setError('');
    
    // Reset UI state
    setMainFiltersExpanded(true); // Keep main filters expanded after reset
    setOtherFiltersExpanded(false);
    setDateFiltersExpanded(false);
    setMultiLineEnabled(false);
    
    // Reset analytics state (tables, graphs, sorting)
    if (resetAnalytics) {
      resetAnalytics();
    }
    
    clearSearchSession();
  }, [resetAnalytics, abortController]);

  // Input change handlers
  const handleInputChange = useCallback((field, value) => {
    setInputValues(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleInputBlur = useCallback((field) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: inputValues[field]
    }));
  }, [inputValues]);

  // Well group filter toggle
  const toggleWellGroupFilter = useCallback((group) => {
    setWellGroupFilters(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  }, []);

  // Multi-line toggle
  const toggleMultiLine = useCallback(() => {
    setMultiLineEnabled(prev => !prev);
  }, []);

  // Load session data
  const loadSessionData = useCallback(() => {
    const sessionData = loadSearchSession();
    if (sessionData) {
      // Validate that session state matches route dataset
      const stateFromRoute = datasetFromRoute ? 
        (datasetFromRoute.charAt(0).toUpperCase() + datasetFromRoute.slice(1).toLowerCase()) : null;
      
      // If route dataset doesn't match session state, clear session for this route
      if (stateFromRoute && sessionData.searchParams?.state && 
          sessionData.searchParams.state !== stateFromRoute) {
        logger.debug(`Session state (${sessionData.searchParams.state}) doesn't match route (${stateFromRoute}), clearing session`);
        clearSearchSession();
        return;
      }
      
      // Hybrid approach: If searchResults exist, restore them (full restore)
      // If not (fallback mode), restore parameters only (user can re-run search)
      if (sessionData.searchResults) {
        // Full restore - results included
        setSearchResults(sessionData.searchResults);
      } else if (sessionData._fallbackMode) {
        // Fallback mode - only parameters saved
        logger.debug('Session restored in fallback mode - parameters only');
      }
      
      // Always restore parameters and UI state
      if (sessionData.searchParams) {
        // Ensure state matches route
        const paramsToRestore = stateFromRoute ? 
          { ...sessionData.searchParams, state: stateFromRoute } : 
          sessionData.searchParams;
        
        setSearchParams(paramsToRestore);
        setInputValues({
          operator_name: paramsToRestore.operator_name || '',
          operator_number: paramsToRestore.operator_number || '',
          lease_name: paramsToRestore.lease_name || '',
          lease_number: paramsToRestore.lease_number || '',
          county: paramsToRestore.county || '',
          api_number: paramsToRestore.api_number || '',
        });
      }
      if (sessionData.activityDateFilters) {
        setActivityDateFilters(sessionData.activityDateFilters);
      }
      if (sessionData.wellDateFilters) {
        setWellDateFilters(sessionData.wellDateFilters);
      }
      if (sessionData.wellGroupFilters) {
        setWellGroupFilters(sessionData.wellGroupFilters);
      }
      if (sessionData.dateFiltersExpanded !== undefined) {
        setDateFiltersExpanded(sessionData.dateFiltersExpanded);
      }
      if (sessionData.mainFiltersExpanded !== undefined) {
        setMainFiltersExpanded(sessionData.mainFiltersExpanded);
      } else {
        setMainFiltersExpanded(true); // Default to expanded if no session data
      }
      if (sessionData.otherFiltersExpanded !== undefined) {
        setOtherFiltersExpanded(sessionData.otherFiltersExpanded);
      }
      if (sessionData.multiLineEnabled !== undefined) {
        setMultiLineEnabled(sessionData.multiLineEnabled);
      }
    }
  }, [datasetFromRoute]);

  return {
    // State
    searchResults,
    loading,
    error,
    searchParams,
    inputValues,
    activityDateFilters,
    wellDateFilters,
    wellGroupFilters,
    mainFiltersExpanded,
    otherFiltersExpanded,
    dateFiltersExpanded,
    multiLineEnabled,
    
    // Setters
    setSearchParams,
    setInputValues,
    setActivityDateFilters,
    setWellDateFilters,
    setWellGroupFilters,
    setMainFiltersExpanded,
    setOtherFiltersExpanded,
    setDateFiltersExpanded,
    setMultiLineEnabled,
    
    // Functions
    handleSearch,
    handleReset,
    cancelSearch,
    handleInputChange,
    handleInputBlur,
    toggleWellGroupFilter,
    toggleMultiLine,
    validateSearchParameters,
    loadSessionData,
    
    // Session utilities
    saveSearchSession,
    loadSearchSession,
    clearSearchSession,
  };
};

export default useSearchData;